<template>
  <div v-if="show" class="filter-column-selector-popup">
    <div class="popup-overlay" @click="$emit('close')"></div>
    <div class="popup-content">
      <div class="popup-header">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Select filter fields</h6>
          <div class="d-flex align-items-center">
            <div class="form-check me-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="select-all-filter-columns"
                :checked="areAllTempColumnsSelected"
                @change="handleToggleAllColumns"
              />
              <label
                class="form-check-label select-all-label"
                for="select-all-filter-columns"
              >
                Select all
              </label>
            </div>
            <button class="btn-close" @click="$emit('close')">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="column-count mt-2">
          <span class="count-badge">
            <i class="fas fa-check me-1"></i>
            {{ selectedColumnsCount }} selected fields
          </span>
        </div>
        <div class="search-box mt-2">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model.trim="searchQuery"
            type="text"
            class="form-control form-control-sm"
            :placeholder="$t('Search fields...')"
          />
        </div>
      </div>

      <div class="popup-body">
        <!-- Campos normales (no reference, no group) -->
        <div
          v-for="field in filteredSimpleFields"
          :key="field.key"
          class="column-item"
        >
          <div class="column-toggle">
            <input
              class="form-check-input"
              type="checkbox"
              :id="'filter-col-' + field.key"
              v-model="tempSelectedColumns[field.key]"
            />
            <label class="form-check-label" :for="'filter-col-' + field.key">
              {{ $t(field.label) }}
              <span class="badge badge-warning" style="font-size: 0.6rem; margin-left: 4px;">
                {{field.type}}
              </span>
            </label>
          </div>
          <div class="column-handle">
            <i class="fas fa-grip-vertical"></i>
          </div>
        </div>

        <!-- Campos tipo reference (expandibles) -->
        <div
          v-for="field in filteredReferenceFields"
          :key="field.key"
          class="group-section reference-section"
        >
          <div class="column-item">
            <div class="column-toggle">
              <input
                class="form-check-input"
                type="checkbox"
                :id="'filter-ref-' + field.key"
                v-model="tempSelectedColumns[field.key]"
              />
              <label
                class="form-check-label"
                :for="'filter-ref-' + field.key"
              >
                {{ $t(field.label) }}
                <span class="badge badge-info" style="font-size: 0.6rem; margin-left: 4px;">
                  reference
                </span>
              </label>
            </div>
            <div class="column-actions">
              <button 
                class="btn-expand"
                @click="toggleReferenceExpand(field)"
                :title="expandedReferences[field.key] ? 'Collapse' : 'Expand to filter by nested fields'"
              >
                <i :class="expandedReferences[field.key] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              </button>
            </div>
          </div>

          <!-- Subcampos del reference (cuando está expandido) -->
          <div v-if="expandedReferences[field.key]" class="sub-columns reference-sub-columns">
            <div v-if="loadingReferenceFields[field.key]" class="loading-indicator">
              <i class="fas fa-spinner fa-spin"></i> Loading fields...
            </div>
            <div
              v-else-if="getFilteredReferenceSubFields(field.key).length > 0"
              v-for="subField in getFilteredReferenceSubFields(field.key)"
              :key="subField.key"
              class="column-item sub-item"
            >
              <div class="column-toggle">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="'filter-col-' + field.key + '-' + subField.key"
                  v-model="tempSelectedColumns[`${field.key}.${subField.key}`]"
                />
                <label
                  class="form-check-label"
                  :for="'filter-col-' + field.key + '-' + subField.key"
                >
                  {{ $t(subField.label) }}
                  <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                    {{subField.type}}
                  </span>
                </label>
              </div>
            </div>
            <div v-else class="no-fields-message">
              No filterable fields found
            </div>
          </div>
        </div>

        <!-- Campos tipo grupo -->
        <div
          v-for="field in filteredGroupFields"
          :key="field.key"
          class="group-section"
        >
          <div class="column-item">
            <div class="column-toggle">
              <input
                class="form-check-input"
                type="checkbox"
                :id="'filter-group-' + field.key"
                v-model="tempSelectedColumns[field.key]"
                @change="toggleGroupFields(field, field.key)"
              />
              <label
                class="form-check-label"
                :for="'filter-group-' + field.key"
              >
                <strong>{{ $t(field.label) }}</strong>
              </label>
            </div>
            <div class="column-handle">
              <i class="fas fa-grip-vertical"></i>
            </div>
          </div>

          <div class="sub-columns">
            <template v-for="subField in sortedSubFields(field.fields)">
              <!-- Si el subcampo es un grupo, renderizar recursivamente -->
              <div
                v-if="subField.type === 'group'"
                :key="'nested-group-' + subField.key"
                class="nested-group-section"
              >
                <div class="column-item sub-item nested-group-header">
                  <div class="column-toggle">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :id="'filter-col-' + field.key + '-' + subField.key"
                      v-model="tempSelectedColumns[`${field.key}.${subField.key}`]"
                      @change="toggleNestedGroupFields(subField, `${field.key}.${subField.key}`, field)"
                    />
                    <label
                      class="form-check-label"
                      :for="'filter-col-' + field.key + '-' + subField.key"
                    >
                      <strong>{{ $t(subField.label) }}</strong>
                      <span class="badge badge-primary" style="font-size: 0.6rem; margin-left: 4px;">
                        group
                      </span>
                    </label>
                  </div>
                  <div class="column-handle">
                    <i class="fas fa-grip-vertical"></i>
                  </div>
                </div>
                
                <!-- Campos dentro del grupo anidado (2do nivel) -->
                <div class="sub-columns nested-level-2">
                  <template v-for="nestedField in sortedSubFields(subField.fields || [])">
                    <!-- Si hay un 3er nivel de grupo -->
                    <div
                      v-if="nestedField.type === 'group'"
                      :key="'deep-group-' + nestedField.key"
                      class="nested-group-section level-3"
                    >
                      <div class="column-item sub-item nested-group-header">
                        <div class="column-toggle">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="'filter-col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                            v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}`]"
                            @change="toggleDeepNestedGroupFields(nestedField, `${field.key}.${subField.key}.${nestedField.key}`, subField, field)"
                          />
                          <label
                            class="form-check-label"
                            :for="'filter-col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                          >
                            <strong>{{ $t(nestedField.label) }}</strong>
                            <span class="badge badge-primary" style="font-size: 0.6rem; margin-left: 4px;">
                              group
                            </span>
                          </label>
                        </div>
                      </div>
                      
                      <!-- Campos dentro del grupo de 3er nivel -->
                      <div class="sub-columns nested-level-3">
                        <div
                          v-for="deepField in sortedSubFields(nestedField.fields || [])"
                          :key="'deep-field-' + deepField.key"
                          class="column-item sub-item"
                        >
                          <div class="column-toggle">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="'filter-col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key"
                              v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}.${deepField.key}`]"
                              @change="updateDeepGroupSelection(nestedField, `${field.key}.${subField.key}.${nestedField.key}`, subField, `${field.key}.${subField.key}`, field)"
                            />
                            <label
                              class="form-check-label"
                              :for="'filter-col-' + field.key + '-' + subField.key + '-' + nestedField.key + '-' + deepField.key"
                            >
                              {{ $t(deepField.label) }}
                              <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                                {{deepField.type}}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Campo normal dentro del grupo anidado -->
                    <div
                      v-else
                      :key="'nested-field-' + nestedField.key"
                      class="column-item sub-item"
                    >
                      <div class="column-toggle">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :id="'filter-col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                          v-model="tempSelectedColumns[`${field.key}.${subField.key}.${nestedField.key}`]"
                          @change="updateNestedGroupSelection(subField, `${field.key}.${subField.key}`, field)"
                        />
                        <label
                          class="form-check-label"
                          :for="'filter-col-' + field.key + '-' + subField.key + '-' + nestedField.key"
                        >
                          {{ $t(nestedField.label) }}
                          <span class="badge badge-secondary" style="font-size: 0.6rem; margin-left: 4px;">
                            {{nestedField.type}}
                          </span>
                        </label>
                      </div>
                      <div class="column-handle">
                        <i class="fas fa-grip-vertical"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
              
              <!-- Campo normal (no grupo) -->
              <div
                v-else
                :key="'field-' + subField.key"
                class="column-item sub-item"
              >
                <div class="column-toggle">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'filter-col-' + field.key + '-' + subField.key"
                    v-model="tempSelectedColumns[`${field.key}.${subField.key}`]"
                    @change="updateGroupSelection(field)"
                  />
                  <label
                    class="form-check-label"
                    :for="'filter-col-' + field.key + '-' + subField.key"
                  >
                    {{ $t(subField.label) }}
                    <span class="badge badge-warning" style="font-size: 0.6rem; margin-left: 4px;">
                      {{subField.type}}
                    </span>
                  </label>
                </div>
                <div class="column-handle">
                  <i class="fas fa-grip-vertical"></i>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div v-if="!hasVisibleFields" class="no-fields-message">
          {{ $t('No fields found for "{query}"', { query: searchQuery }) }}
        </div>
      </div>

      <div class="popup-footer">
        <button class="btn btn-outline-secondary btn-sm" @click="cancelChanges">
          Cancel
        </button>
        <button class="btn btn-primary btn-sm" @click="applyChanges">
          Apply
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";

export default {
  name: "FilterColumnSelector",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    tableFields: {
      type: Array,
      required: true,
    },
    selectedColumns: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tempSelectedColumns: {},
      expandedReferences: {},
      referenceSubFields: {},
      loadingReferenceFields: {},
      searchQuery: "",
    };
  },
  watch: {
    show: {
      handler(newVal) {
        if (newVal) {
          // Emitir evento para que el componente padre cargue las preferencias
          this.$emit("popup-opened");
          this.searchQuery = "";

          // Inicializar las columnas temporales con las seleccionadas actuales
          if (Object.keys(this.selectedColumns).length > 0) {
            this.tempSelectedColumns = JSON.parse(
              JSON.stringify(this.selectedColumns)
            );
          }
        }
      },
      immediate: false,
    },
    selectedColumns: {
      handler(newVal) {
        // Cuando cambien las columnas seleccionadas (por ejemplo, al cargar preferencias)
        // actualizar las columnas temporales si el popup está abierto
        if (this.show && Object.keys(newVal).length > 0) {
          this.tempSelectedColumns = JSON.parse(JSON.stringify(newVal));
        }
        // Forzar la actualización del componente
        this.$forceUpdate();
      },
      deep: true,
    },
  },
  computed: {
    normalizedSearchTerm() {
      return this.normalizeText(this.searchQuery);
    },
    // Campos simples (no reference expandible, no group) - incluye references a users
    simpleFields() {
      return this.tableFields
        .filter((field) => {
          if (field.hideDisplay) return false;
          if (field.type === "group") return false;
          if (field.type === "image") return false;
          // Referencias a users van en campos simples (sin opción de expandir)
          if (field.type === "reference" && field.ref === "users") return true;
          // Otras referencias van en su propia sección (expandibles)
          if (field.type === "reference") return false;
          return true;
        })
        .sort((a, b) => {
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    filteredSimpleFields() {
      if (!this.normalizedSearchTerm) return this.simpleFields;
      return this.simpleFields.filter((field) => this.fieldMatchesSearch(field));
    },
    // Campos de tipo reference (expandibles) - excluye referencias a users
    referenceFields() {
      return this.tableFields
        .filter((field) => field.type === "reference" && !field.hideDisplay && field.ref && field.ref !== "users")
        .sort((a, b) => {
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    filteredReferenceFields() {
      if (!this.normalizedSearchTerm) return this.referenceFields;
      return this.referenceFields.filter((field) => {
        if (this.fieldMatchesSearch(field)) return true;
        const refSubFields = this.getFilteredReferenceSubFields(field.key);
        return refSubFields.length > 0;
      });
    },
    // Para compatibilidad con código existente
    nonGroupFields() {
      return this.tableFields
        .filter(
          (field) =>
            field.type !== "group" &&
            field.type !== "image" &&
            !field.hideDisplay
        )
        .sort((a, b) => {
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    groupFields() {
      return this.tableFields
        .filter((field) => field.type === "group" && !field.hideDisplay)
        .sort((a, b) => {
          const labelA = (a.label || a.key).toLowerCase();
          const labelB = (b.label || b.key).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    },
    filteredGroupFields() {
      if (!this.normalizedSearchTerm) return this.groupFields;
      return this.groupFields
        .map((field) => this.filterGroupFieldForSearch(field))
        .filter((field) => Boolean(field));
    },
    hasVisibleFields() {
      return (
        this.filteredSimpleFields.length > 0 ||
        this.filteredReferenceFields.length > 0 ||
        this.filteredGroupFields.length > 0
      );
    },
    areAllTempColumnsSelected() {
      // Verificar si todos los campos normales están seleccionados
      const allNormalFieldsSelected = this.nonGroupFields.every(
        (field) => this.tempSelectedColumns[field.key] === true
      );

      // Función recursiva para verificar campos anidados
      const allNestedSelected = (fields, parentPath) => {
        return fields.every((subField) => {
          if (subField.hideDisplay) return true;
          const fieldPath = `${parentPath}.${subField.key}`;
          
          if (this.tempSelectedColumns[fieldPath] !== true) {
            return false;
          }
          
          // Verificar campos anidados si es un grupo
          if (subField.type === 'group' && subField.fields) {
            return allNestedSelected(subField.fields, fieldPath);
          }
          
          return true;
        });
      };

      // Verificar si todos los campos de grupo están seleccionados recursivamente
      const allGroupFieldsSelected = this.groupFields.every((groupField) => {
        if (this.tempSelectedColumns[groupField.key] !== true) {
          return false;
        }
        return allNestedSelected(groupField.fields || [], groupField.key);
      });

      return allNormalFieldsSelected && allGroupFieldsSelected;
    },
    selectedColumnsCount() {
      let count = 0;

      // Contar campos normales seleccionados
      this.nonGroupFields.forEach((field) => {
        if (this.tempSelectedColumns[field.key] === true) {
          count++;
        }
      });

      // Función recursiva para contar campos anidados
      const countNested = (fields, parentPath) => {
        let nestedCount = 0;
        fields.forEach((subField) => {
          if (!subField.hideDisplay) {
            const fieldPath = `${parentPath}.${subField.key}`;
            if (this.tempSelectedColumns[fieldPath] === true) {
              nestedCount++;
            }
            // Contar campos anidados si es un grupo
            if (subField.type === 'group' && subField.fields) {
              nestedCount += countNested(subField.fields, fieldPath);
            }
          }
        });
        return nestedCount;
      };

      // Contar campos de grupo y subcampos seleccionados recursivamente
      this.groupFields.forEach((field) => {
        if (this.tempSelectedColumns[field.key] === true) {
          count++;
        }
        count += countNested(field.fields || [], field.key);
      });

      return count;
    },
  },
  methods: {
    normalizeText(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    fieldMatchesSearch(field, pathPrefix = "") {
      const term = this.normalizedSearchTerm;
      if (!term) return true;
      const path = pathPrefix ? `${pathPrefix}.${field.key}` : field.key;
      const searchableText = [
        this.$t(field.label || ""),
        field.label || "",
        field.key || "",
        field.type || "",
        path,
      ]
        .map((v) => this.normalizeText(v))
        .join(" ");
      return searchableText.includes(term);
    },
    filterGroupFieldForSearch(field, parentPath = "") {
      const currentPath = parentPath ? `${parentPath}.${field.key}` : field.key;
      const selfMatches = this.fieldMatchesSearch(field, parentPath);
      if (!field.fields || field.fields.length === 0) {
        return selfMatches ? { ...field } : null;
      }

      const filteredChildren = field.fields
        .map((subField) => this.filterGroupFieldForSearch(subField, currentPath))
        .filter((subField) => Boolean(subField));

      if (selfMatches || filteredChildren.length > 0) {
        return {
          ...field,
          fields: filteredChildren,
        };
      }
      return null;
    },
    getFilteredReferenceSubFields(referenceKey) {
      const subFields = (this.referenceSubFields[referenceKey] || []).filter(
        (s) => s.type !== "image"
      );
      if (!this.normalizedSearchTerm) return subFields;
      return subFields.filter((subField) =>
        this.fieldMatchesSearch(subField, referenceKey)
      );
    },
    sortedSubFields(fields) {
      return [...fields].sort((a, b) => {
        const labelA = (a.label || a.key).toLowerCase();
        const labelB = (b.label || b.key).toLowerCase();
        return labelA.localeCompare(labelB);
      });
    },
    applyChanges() {
      // Crear una copia de las columnas temporales para emitir
      const updatedColumns = JSON.parse(JSON.stringify(this.tempSelectedColumns));
      
      // Emitir el evento con los nuevos valores para que el padre actualice selectedFilterFields
      this.$emit("columns-changed", updatedColumns);
      // Emitir evento para guardar las preferencias
      this.$emit("save-preferences");
      this.$emit("close");
    },
    cancelChanges() {
      // Restaurar las columnas originales
      this.tempSelectedColumns = JSON.parse(
        JSON.stringify(this.selectedColumns)
      );
      this.$emit("close");
    },
    handleToggleAllColumns(event) {
      if (event.target.checked) {
        this.selectAllColumns();
      } else {
        this.deselectAllColumns();
      }
    },
    selectAllColumns() {
      // Seleccionar todos los campos normales
      this.nonGroupFields.forEach((field) => {
        this.$set(this.tempSelectedColumns, field.key, true);
      });

      // Función recursiva para seleccionar campos anidados
      const selectAllNested = (fields, parentPath) => {
        fields.forEach((subField) => {
          if (!subField.hideDisplay && subField.type !== "image") {
            const fieldPath = `${parentPath}.${subField.key}`;
            this.$set(this.tempSelectedColumns, fieldPath, true);
            
            // Si es un grupo, también seleccionar sus campos
            if (subField.type === 'group' && subField.fields) {
              selectAllNested(subField.fields, fieldPath);
            }
          }
        });
      };

      // Seleccionar todos los campos de grupo y sus subcampos recursivamente
      this.groupFields.forEach((field) => {
        this.$set(this.tempSelectedColumns, field.key, true);
        selectAllNested(field.fields || [], field.key);
      });

      // Forzar la actualización de la UI
      this.$forceUpdate();
    },
    deselectAllColumns() {
      // Deseleccionar todos los campos normales
      this.nonGroupFields.forEach((field) => {
        this.$set(this.tempSelectedColumns, field.key, false);
      });

      // Función recursiva para deseleccionar campos anidados
      const deselectAllNested = (fields, parentPath) => {
        fields.forEach((subField) => {
          if (!subField.hideDisplay && subField.type !== "image") {
            const fieldPath = `${parentPath}.${subField.key}`;
            this.$set(this.tempSelectedColumns, fieldPath, false);
            
            // Si es un grupo, también deseleccionar sus campos
            if (subField.type === 'group' && subField.fields) {
              deselectAllNested(subField.fields, fieldPath);
            }
          }
        });
      };

      // Deseleccionar todos los campos de grupo y sus subcampos recursivamente
      this.groupFields.forEach((field) => {
        this.$set(this.tempSelectedColumns, field.key, false);
        deselectAllNested(field.fields || [], field.key);
      });

      // Forzar la actualización de la UI
      this.$forceUpdate();
    },
    toggleGroupFields(field, basePath) {
      const isSelected = this.tempSelectedColumns[basePath];
      
      // Función recursiva para toggle de todos los campos anidados
      const toggleAllNested = (fields, parentPath) => {
        fields.forEach((subField) => {
          if (!subField.hideDisplay && subField.type !== "image") {
            const fieldPath = `${parentPath}.${subField.key}`;
            this.$set(this.tempSelectedColumns, fieldPath, isSelected);
            
            // Si es un grupo anidado, también toggle sus campos
            if (subField.type === 'group' && subField.fields) {
              toggleAllNested(subField.fields, fieldPath);
            }
          }
        });
      };
      
      toggleAllNested(field.fields || [], basePath);
    },
    
    toggleNestedGroupFields(nestedGroup, basePath, parentField) {
      const isSelected = this.tempSelectedColumns[basePath];
      
      // Toggle todos los campos dentro del grupo anidado
      const toggleAllNested = (fields, parentPath) => {
        fields.forEach((subField) => {
          if (!subField.hideDisplay && subField.type !== "image") {
            const fieldPath = `${parentPath}.${subField.key}`;
            this.$set(this.tempSelectedColumns, fieldPath, isSelected);
            
            // Si es un grupo anidado, también toggle sus campos
            if (subField.type === 'group' && subField.fields) {
              toggleAllNested(subField.fields, fieldPath);
            }
          }
        });
      };
      
      toggleAllNested(nestedGroup.fields || [], basePath);
      
      // Actualizar selección del grupo padre
      this.updateGroupSelection(parentField);
    },
    
    toggleDeepNestedGroupFields(deepGroup, basePath, parentGroup, rootField) {
      const isSelected = this.tempSelectedColumns[basePath];
      
      // Toggle todos los campos dentro del grupo profundo
      (deepGroup.fields || []).forEach((subField) => {
        if (!subField.hideDisplay && subField.type !== "image") {
          this.$set(this.tempSelectedColumns, `${basePath}.${subField.key}`, isSelected);
        }
      });
      
      // Actualizar selección de grupos padres
      const parentPath = basePath.split('.').slice(0, -1).join('.');
      this.updateNestedGroupSelection(parentGroup, parentPath, rootField);
    },
    
    updateGroupSelection(field) {
      // Verificar si algún campo directo o anidado está seleccionado
      const hasSelectedField = (fields, parentPath) => {
        return fields.some((subField) => {
          if (subField.hideDisplay) return false;
          const fieldPath = `${parentPath}.${subField.key}`;
          
          if (this.tempSelectedColumns[fieldPath] === true) {
            return true;
          }
          
          // Verificar campos anidados si es un grupo
          if (subField.type === 'group' && subField.fields) {
            return hasSelectedField(subField.fields, fieldPath);
          }
          
          return false;
        });
      };
      
      const someSelected = hasSelectedField(field.fields || [], field.key);
      this.$set(this.tempSelectedColumns, field.key, someSelected);
    },
    
    updateNestedGroupSelection(nestedGroup, nestedPath, parentField) {
      // Verificar si algún campo del grupo anidado está seleccionado
      const hasSelectedField = (fields, parentPath) => {
        return fields.some((subField) => {
          if (subField.hideDisplay) return false;
          const fieldPath = `${parentPath}.${subField.key}`;
          
          if (this.tempSelectedColumns[fieldPath] === true) {
            return true;
          }
          
          // Verificar campos anidados si es un grupo
          if (subField.type === 'group' && subField.fields) {
            return hasSelectedField(subField.fields, fieldPath);
          }
          
          return false;
        });
      };
      
      const someSelected = hasSelectedField(nestedGroup.fields || [], nestedPath);
      this.$set(this.tempSelectedColumns, nestedPath, someSelected);
      
      // También actualizar el grupo padre
      this.updateGroupSelection(parentField);
    },
    
    updateDeepGroupSelection(deepGroup, deepPath, parentGroup, parentPath, rootField) {
      // Verificar si algún campo del grupo profundo está seleccionado
      const visibleFields = (deepGroup.fields || []).filter(f => !f.hideDisplay);
      const someSelected = visibleFields.some(
        (subField) => this.tempSelectedColumns[`${deepPath}.${subField.key}`] === true
      );
      
      this.$set(this.tempSelectedColumns, deepPath, someSelected);
      
      // Actualizar grupos padres
      this.updateNestedGroupSelection(parentGroup, parentPath, rootField);
    },
    
    // Métodos para campos reference
    async toggleReferenceExpand(field) {
      const isExpanded = this.expandedReferences[field.key];
      this.$set(this.expandedReferences, field.key, !isExpanded);
      
      // Si se está expandiendo y no tenemos los campos cargados, cargarlos
      if (!isExpanded && !this.referenceSubFields[field.key]) {
        await this.loadReferenceFields(field);
      }
    },
    
    async loadReferenceFields(field) {
      if (!field.ref) return;
      
      this.$set(this.loadingReferenceFields, field.key, true);
      
      try {
        // Cargar la definición del modelo referenciado (no los items)
        const response = await wencesApi.get(`/api/gotrading/model-definitions/${field.ref}`);
        
        if (response.data && response.data.fields) {
          // Filtrar solo campos que se pueden usar como filtro (no groups)
          const filterableFields = response.data.fields
            .filter(f => 
              !f.hideDisplay && 
              f.type !== 'group' && 
              ['string', 'number', 'date', 'datetime', 'boolean', 'list', 'reference', 'users', 'text', 'email', 'phone', 'autoincrement'].includes(f.type)
            )
            .sort((a, b) => {
              const labelA = (a.label || a.key).toLowerCase();
              const labelB = (b.label || b.key).toLowerCase();
              return labelA.localeCompare(labelB);
            });
          
          this.$set(this.referenceSubFields, field.key, filterableFields);
        } else {
          console.warn(`No fields found in model definition for ${field.ref}`);
          this.$set(this.referenceSubFields, field.key, []);
        }
      } catch (error) {
        console.error(`Error loading reference fields for ${field.ref}:`, error);
        this.$set(this.referenceSubFields, field.key, []);
      } finally {
        this.$set(this.loadingReferenceFields, field.key, false);
      }
    },
  },
};
</script>

<style scoped>
.filter-column-selector-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
}

.popup-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
}

.popup-content {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 350px;
  max-height: 80vh;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.popup-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.popup-header h6 {
  color: #495057;
  font-weight: 600;
}

.search-box {
  position: relative;
}

.search-box .search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.8rem;
}

.search-box .form-control {
  padding-left: 30px;
  border-color: #dee2e6;
}

.select-all-label {
  color: #495057;
  cursor: pointer;
  font-size: 0.875rem;
  margin: 0;
  font-weight: 500;
}

.form-check-input:checked {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.form-check-input:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.count-badge {
  background-color: #6c757d;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
}

.btn-close {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
}

.btn-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.popup-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
  max-height: 400px;
}

.column-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
}

.column-item:last-child {
  border-bottom: none;
}

.column-toggle {
  display: flex;
  align-items: center;
  flex: 1;
}

.column-toggle .form-check-input {
  margin-right: 8px;
}

.column-toggle .form-check-label {
  cursor: pointer;
  margin: 0;
  font-size: 0.875rem;
  color: #495057;
}

.column-handle {
  color: #adb5bd;
  cursor: grab;
  padding: 4px;
}

.column-handle:hover {
  color: #6c757d;
}

.group-section {
  margin-bottom: 16px;
}

.sub-columns {
  margin-left: 20px;
  margin-top: 8px;
}

.sub-item {
  padding: 6px 0;
}

.sub-item .column-toggle .form-check-label {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Estilos para grupos anidados de 2do nivel */
.nested-group-section {
  margin-top: 4px;
  margin-bottom: 8px;
  border-left: 2px solid #28a745;
  padding-left: 8px;
}

.nested-group-header {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 4px 8px !important;
}

.nested-group-header .form-check-label {
  font-size: 0.8rem !important;
  color: #495057 !important;
}

.nested-level-2 {
  margin-left: 12px;
  border-left: 1px dashed #dee2e6;
  padding-left: 8px;
}

.nested-level-2 .column-item {
  padding: 4px 0;
}

.nested-level-2 .form-check-label {
  font-size: 0.75rem !important;
  color: #868e96 !important;
}

/* Estilos para grupos anidados de 3er nivel */
.nested-group-section.level-3 {
  border-left-color: #ffc107;
}

.nested-level-3 {
  margin-left: 12px;
  border-left: 1px dotted #ffc107;
  padding-left: 8px;
}

.nested-level-3 .column-item {
  padding: 3px 0;
}

.nested-level-3 .form-check-label {
  font-size: 0.7rem !important;
  color: #adb5bd !important;
}

.badge-primary {
  background-color: #28a745;
  color: white;
}

/* Estilos para campos reference */
.reference-section {
  border-left: 3px solid #17a2b8;
  padding-left: 8px;
  margin-left: -8px;
}

.reference-sub-columns {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 8px;
  margin-top: 4px;
}

.column-actions {
  display: flex;
  align-items: center;
}

.btn-expand {
  background: none;
  border: 1px solid #dee2e6;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  transition: all 0.2s;
}

.btn-expand:hover {
  background-color: #e9ecef;
  color: #495057;
  border-color: #adb5bd;
}

.loading-indicator {
  color: #6c757d;
  font-size: 0.8rem;
  padding: 8px;
  text-align: center;
}

.no-fields-message {
  color: #adb5bd;
  font-size: 0.8rem;
  font-style: italic;
  padding: 8px;
  text-align: center;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.popup-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.popup-footer .btn {
  flex: 1;
}

.btn-outline-secondary {
  border-color: #17a2b8;
  color: #17a2b8;
}

.btn-outline-secondary:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn-primary {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.btn-primary:hover {
  background-color: #138496;
  border-color: #138496;
}

/* Scrollbar personalizado */
.popup-body::-webkit-scrollbar {
  width: 6px;
}

.popup-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.popup-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.popup-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
